// DataComponent.tsx
import { useEffect, useRef, useState, useCallback } from 'react';
import { ComboBox, Item, Key } from '@adobe/react-spectrum';
import { useAsyncList } from 'react-stately';
import { DataComponentProps } from './DataComponentProps';
import { useDependency } from '../../../contexts/DependencyProvider';
import { usePreloadAssets } from '../../../hooks/UsePreloadAssets';
import { FPAData, FPADataFactory } from '../../../infra/protected/FPA/FPAData';
import { ListFPARequest2 } from '../../../services/soap/project/requests/ListFPARequest2';
import { Col, FilterCol, Order } from '../../../services/soap/project/requests/ListFPARequest';
import { useTranslation } from 'react-i18next';

interface FolderDropDownProps extends Omit<DataComponentProps, 'onValueChange'> {
  folder_type_id?: number;
  parent_folder_id?: number;
  activity_type_id?: number;
  project_type_id?: number;
  flatView: boolean;
  returnFullObject?: boolean;
  onValueChange?: (value: string | Record<string, any>, input?: string) => void;
}

interface LoadDataParams {
    searchText?: string;
    id?: number;
    offset?: number;
    limit?: number;
    countOnly?: boolean;
}

const FolderDropDown: React.FC<FolderDropDownProps> = ({
  folder_type_id,
  parent_folder_id,
  activity_type_id,
  project_type_id,
  compKey,
  label,
  width,
  isRequired,
  onValueChange,
  onBlur,
  value: dvalue,
  flatView,
  returnFullObject = false,
}) => {
  const { t } = useTranslation();
  const _t = (key: string) => t(`newContextDialog.dataComponents.${key}`, { ns: 'finder' });
  const message = _t('is_required').split('[content]').join(label);
  const [errorMessage, setErrorMessage] = useState(isRequired ? message : '');
  const [initialValueLoaded, setInitialValueLoaded] = useState(false);
  const prevDvalueRef = useRef<string | undefined>(dvalue);
  const { projectService, store } = useDependency();
  const { folderStatusMapping, projectStatusMapping, activityStatusMapping } = usePreloadAssets();
  // const [totalCount, setTotalCount] = useState<number>(0);

  // Clear error when a required default value is present
  useEffect(() => {
    if (isRequired && dvalue && dvalue !== 'undefined') {
      setErrorMessage('');
    }
  }, [isRequired, dvalue]);


  const getTotalCount = useCallback(async (searchText?: string): Promise<number> => {
    const folder_filter: FilterCol[] = [];

    if (folder_type_id) {
      folder_filter.push(new FilterCol('folderType', 'equals', folder_type_id.toString()));
    }
    if(parent_folder_id) {
      folder_filter.push(new FilterCol('parent', 'equals', parent_folder_id.toString()));
    }
    if(activity_type_id){
      folder_filter.push(new FilterCol('relatedToProjectsOfActivityType', 'equals', activity_type_id.toString()));
    }
    if(project_type_id){
      folder_filter.push(new FilterCol('relatedToSlaveProjectType', 'equals', project_type_id.toString()));
    }
    const response = await projectService.listFPA2(
      new ListFPARequest2(
          store.Server, store.SessionId,
          {
              countOnly: true,
              fulltext: searchText ? searchText : undefined,
              flatView: flatView,
              folderFilter:{
                  COL: folder_filter
              },
              ORDER: new Order([
                  new Col('name'), 
                  new Col('created', true)
              ])
          }
      )        
    );
    
    if(response.EXCEPTION) {
      console.error(response.EXCEPTION.message);
      return 0;
    } else {
      return response.count || 0;
    }
  }, [folder_type_id, parent_folder_id, flatView, projectService, store]);

  const loadData = useCallback(async ({ searchText, id, offset = 0, limit = 100 }: LoadDataParams) => {
    const folder_filter: FilterCol[] = [];

    if (folder_type_id) {
      folder_filter.push(new FilterCol('folderType', 'equals', folder_type_id.toString()));
    }
    if(parent_folder_id) {
      folder_filter.push(new FilterCol('parent', 'equals', parent_folder_id.toString()));
    }

    if(activity_type_id){
      folder_filter.push(new FilterCol('relatedToProjectsOfActivityType', 'equals', activity_type_id.toString()));
    }
    if(project_type_id){
      folder_filter.push(new FilterCol('relatedToSlaveProjectType', 'equals', project_type_id.toString()));
    }
    
    if(id){
      folder_filter.push(new FilterCol('id', 'equals', id.toString()));
    }

    const response = await projectService.listFPA2(
      new ListFPARequest2(
          store.Server, store.SessionId,
          {
              offset: offset,
              limit: limit,
              fulltext: searchText ? searchText : undefined,
              flatView: flatView,
              folderFilter:{
                  COL: folder_filter
              },
              ORDER: new Order([
                  new Col('name'), 
                  new Col('created', true)
              ])
          }
      )        
    );
    
    if(response.EXCEPTION) {
      console.error(response.EXCEPTION.message);
      return { items: [], totalCount: 0 };
    } else if(response.ITEMS) {
      const result = response.ITEMS.map((item: any) => FPADataFactory.createFromItem(
                                      item, 
                                      folderStatusMapping, 
                                      projectStatusMapping, 
                                      activityStatusMapping));
      return { items: result, totalCount: response.count || result.length };
    } else {
      return { items: [], totalCount: 0 };
    }
  }, [folder_type_id, parent_folder_id, flatView, projectService, store, folderStatusMapping, projectStatusMapping, activityStatusMapping]);

  const list = useAsyncList<FPAData>({
    async load({ cursor, filterText }) {
      const searchText = filterText || '';
      const cursor_obj = cursor ? JSON.parse(cursor) : null;
      const offset = cursor_obj ? cursor_obj.offset : 0;
      const limit = 50; // Smaller page size for better performance
      
      try {
        let totalCount = 0;
        if(cursor_obj && cursor_obj.total) {
          totalCount = cursor_obj.total || 0;
        } else {
          // First get total count for the current search
          totalCount = await getTotalCount(searchText.length >= 3 ? `${searchText}*` : undefined);
        }
        
        // Then load the actual data with pagination
        const result = await loadData({
          searchText: searchText.length >= 3 ? `${searchText}*` : undefined,
          offset: offset,
          limit: limit
        });

        return {
          items: result.items,
          cursor: offset + limit <= totalCount ? 
          JSON.stringify({offset: offset + limit, total: totalCount}) : undefined
        };
      } catch (error) {
        console.error('Error loading data:', error);
        return {
          items: [],
          cursor: undefined
        };
      }
    },
  });

  let onInputChange = (value: string) => {
    if(value === ''){
      list.setSelectedKeys(new Set([]));
      if(onValueChange)
          onValueChange('', '');
      setErrorMessage('');
    }

    list.setFilterText(value);
  }

  let onSelectionChange = (key: Key | null) => {
    if (key) {
      const item = list.getItem(key);
      const itemText = item?.title;
      list.setSelectedKeys(new Set([key]));
      list.setFilterText(itemText || '');

      if (onValueChange) {
        if (returnFullObject && item) {
          onValueChange(item, itemText || '');
        } else {
          onValueChange(key.toString(), itemText || '');
        }
      }
      if (isRequired && !key) setErrorMessage(message);
      else setErrorMessage('');
    } else {
      list.setSelectedKeys(new Set([]));
      list.setFilterText('');
      if (onValueChange) onValueChange('', '');
      if (isRequired) setErrorMessage(message);
      else setErrorMessage('');
    }
  };
  const dvalueId =
    typeof dvalue === 'object' && dvalue !== null && 'id' in dvalue
      ? String((dvalue as { id: string | number }).id)
      : dvalue;

  // Load initial value when component mounts
  useEffect(() => {
    if (dvalueId && dvalueId !== 'undefined' && !initialValueLoaded) {
      loadData({ id: parseInt(dvalueId) }).then((result) => {
        if (result.items.length > 0) {
          const item = result.items[0];
          list.setSelectedKeys(new Set([dvalueId]));
          list.setFilterText(item.title);
          setErrorMessage('');
          if (onValueChange) {
            if (returnFullObject) onValueChange(item, item.title);
            else onValueChange(dvalueId, item.title);
          }
          setInitialValueLoaded(true);
        }
      }).catch((error) => {
        console.error('Error loading initial value:', error);
        setInitialValueLoaded(true);
      });
    }
  }, [dvalueId, initialValueLoaded, onValueChange, loadData, list, returnFullObject]);

  // Handle value changes after initial load
  useEffect(() => {
    if (dvalueId !== prevDvalueRef.current && initialValueLoaded) {
      prevDvalueRef.current = dvalueId;

      if (dvalueId && dvalueId !== 'undefined') {
        const existingItem = list.items.find((item) => item.id.toString() === dvalueId);
        if (existingItem) {
          list.setSelectedKeys(new Set([dvalueId]));
          list.setFilterText(existingItem.title);
          setErrorMessage('');
          if (onValueChange) {
            if (returnFullObject) onValueChange(existingItem, existingItem.title);
            else onValueChange(dvalueId, existingItem.title);
          }
        } else {
          loadData({ id: parseInt(dvalueId) }).then((result) => {
            if (result.items.length > 0) {
              const item = result.items[0];
              list.setSelectedKeys(new Set([dvalueId]));
              list.setFilterText(item.title);
              setErrorMessage('');
              if (onValueChange) {
                if (returnFullObject) onValueChange(item, item.title);
                else onValueChange(dvalueId, item.title);
              }
            }
          });
        }
      } else if (!dvalueId || dvalueId === 'undefined') {
        // Clear selection if value is empty
        list.setSelectedKeys(new Set([]));
        list.setFilterText('');
        if (onValueChange) {
          onValueChange('', '');
        }
      }
    }
  }, [dvalueId, initialValueLoaded, onValueChange, loadData, list, returnFullObject]);

  let selectedKeys = list.selectedKeys !== 'all' ? list.selectedKeys.values().next().value : null;
  return (
    <ComboBox
      key={compKey}
      label={label}
      width={width}
      isRequired={isRequired}
      selectedKey={selectedKeys ?? null}
      onSelectionChange={onSelectionChange}
      onBlur={(e) => {
        if (onBlur) onBlur();
      }}
      validationState={errorMessage ? 'invalid' : undefined}
      errorMessage={errorMessage}
      items={list.items}
      inputValue={list.filterText || ''}
      onInputChange={onInputChange}
      loadingState={list.loadingState}
      onLoadMore={list.loadMore}
    >
      {(item: FPAData) => (
        <Item key={item.id} textValue={item.title}>
          {item.title}
        </Item>
      )}
    </ComboBox>
  );
};

export default FolderDropDown;
